<script lang="ts">
  import type { ShapeLink } from '$lib/sanity/schemas';
  import { TwistGroup, ResponsiveVars } from '@twist/core';
  import { isHrefExternal, isDocumenHref } from '$lib/utils/links';
  import { getIconByShape } from '$components/News/ButtonShapes';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  import HeadingSizeDecorator from '$components/Common/HeadingSizeDecorator.svelte';
  import FontSizeDecorator from '../PortableText/Decorators/FontSizeDecorator.svelte';

  interface Props {
    document: ShapeLink;
    sourceMap?: ContentSourceMap | undefined;
    path?: StudioPathLike;
  }

  let { document, sourceMap = undefined, path = [] }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const shouldOpenInNewTab = isHrefExternal(document.link.url) || isDocumenHref(document.link.url);

  const responsiveVars = new ResponsiveVars()
    .addFluid('eyebrow-mb', toFluidVarInput(10.5, 12))
    .addFluid('spacer', toFluidVarInput(36, 26))
    .addFluid('font-size', toFluidVarInput(14, 18))
    .addFluid('padding-y', toFluidVarInput(24, 36))
    .toCSS();
</script>

<TwistGroup>
  {#snippet children({ active: groupActive }: { active: boolean })}
    <a
      class={responsiveVars}
      href={document.link.url}
      target={shouldOpenInNewTab ? '_blank' : undefined}
      data-sanity={encodeDataAttribute([...path, 'text'])}
    >
      <div class="link">
        {#if document.eyebrowText}
          <span class="eyebrow">
            <FontSizeDecorator size="breadcrumb">
              {document.eyebrowText}
            </FontSizeDecorator>
          </span>
        {/if}
        <span>
          <HeadingSizeDecorator size="h6">
            {document.link.text}
          </HeadingSizeDecorator>
        </span>
      </div>

      <BlockButton
        {groupActive}
        href={document.link.url}
        target={shouldOpenInNewTab ? '_blank' : undefined}
        motion="when-active"
        aria-hidden="true"
        tabindex={-1}
      >
        {#snippet icon()}
          {@const SvelteComponent = getIconByShape(document.shape)}
          <SvelteComponent />
        {/snippet}
      </BlockButton>
    </a>
  {/snippet}
</TwistGroup>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/button';

  a {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: -1px;
    border-top: 1px solid var(--theme-general-foreground-color, $border-depth-1-color);
    border-bottom: 1px solid var(--theme-general-foreground-color, $border-depth-1-color);
    padding: var(--padding-y) 0;
    font-size: var(--font-size);
    font-family: var(--font-family-primary);
    font-weight: 700;
    line-height: 130%;
    color: var(
      --theme-text-primary-button-default-background-color,
      $text-interactive-default-color
    );
    text-decoration: none;
    cursor: pointer;

    @include buttonHoverFocus {
      color: var(
        --theme-button-primary-button-hover-focus-background-color,
        $text-interactive-default-hover-color
      );
    }

    &:active {
      color: var(
        --theme-button-primary-button-hover-focus-background-color,
        $text-interactive-default-hover-color
      );
      opacity: 0.6;
    }
  }

  span {
    display: block;
    margin-right: var(--spacer);
  }

  .link {
    display: flex;
    flex-direction: column;

    .eyebrow {
      margin-bottom: var(--eyebrow-mb);
    }
  }
</style>
